import { useFormik } from 'formik'
import { useState } from 'react'
import { Col, Form, FormLabel, Row, Spinner } from 'react-bootstrap'
import * as Yup from 'yup'
import { useGetModelsQuery, useTestMutation, useTrainMutation } from '../../redux/api/pipelineApi.js'
import ResultsTable from './ResultsTable.jsx'

// Validation Schema
const validationSchema = Yup.object().shape({
  type: Yup.string().oneOf(['train', 'test']).required('Select type'),
  model: Yup.string().required('Model is required'),
  target_column: Yup.string().required('Target column is required'),
  train_percent: Yup.number().when('type', {
    is: 'train',
    then: (schema) => schema.required('Train percent required').min(0, 'Must be >= 0').max(100, 'Must be <= 100'),
    otherwise: (schema) => schema.strip(),
  }),
  test_percent: Yup.number().when('type', {
    is: 'train',
    then: (schema) =>
      schema
        .required('Test percent required')
        .min(0, 'Must be >= 0')
        .max(100, 'Must be <= 100')
        .test('sum-100', 'Train + Test percent must equal 100', function (value) {
          const { train_percent } = this.parent
          if (train_percent == null || value == null) return true
          return train_percent + value === 100
        }),
    otherwise: (schema) => schema.strip(),
  }),
})

const TrainTestForm = ({ setStep }) => {
  const [data, setData] = useState(null)

  const { data: models = null, isLoading: loadingModels } = useGetModelsQuery()
  const [train, { isLoading: training }] = useTrainMutation()
  const [test, { isLoading: testing }] = useTestMutation()

  const handleSubmit = async (values, { setFieldError }) => {
    try {
      if (values.type === 'train') {
        const payload = {
          model: values.model,
          target_column: values.target_column,
          train_percent: values.train_percent,
          test_percent: values.test_percent,
        }
        const result = await train({ data: payload }).unwrap()
        setData(result)
      } else {
        const payload = {
          model: values.model,
          target_column: values.target_column,
        }
        const result = await test({ data: payload }).unwrap()
        setData(result)
      }
    } catch (err) {
      setFieldError('form', err?.data?.error || err?.data?.message || 'An error occurred during upload')
    }
  }

  const formik = useFormik({
    initialValues: {
      type: '',
      model: '',
      target_column: '',
      train_percent: 80,
      test_percent: 20,
    },
    validationSchema,
    onSubmit: handleSubmit,
  })

  const isSubmitting = training || testing

  return (
    <>
      <Form onSubmit={formik.handleSubmit}>
        {/* Type Selector */}
        <Form.Group className='mb-3'>
          <FormLabel>Type</FormLabel>
          <Form.Select name='type' value={formik.values.type} onChange={formik.handleChange} onBlur={formik.handleBlur}>
            <option value=''>Select</option>
            <option value='train'>Train</option>
            <option value='test'>Test</option>
          </Form.Select>
          {formik.touched.type && formik.errors.type && <div className='text-danger'>{formik.errors.type}</div>}
        </Form.Group>

        {/* Model Selector */}
        <Form.Group className='mb-3'>
          <FormLabel>Model</FormLabel>
          <Form.Select name='model' value={formik.values.model} onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={loadingModels}>
            <option value=''>Select a model</option>
            {models &&
              Object.keys(models?.available_models).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
          </Form.Select>
          {formik.touched.model && formik.errors.model && <div className='text-danger'>{formik.errors.model}</div>}
        </Form.Group>

        {/* Target Column */}
        <Form.Group className='mb-3'>
          <FormLabel>Target Column</FormLabel>
          <Form.Control type='text' name='target_column' value={formik.values.target_column} onChange={formik.handleChange} onBlur={formik.handleBlur} />
          {formik.touched.target_column && formik.errors.target_column && <div className='text-danger'>{formik.errors.target_column}</div>}
        </Form.Group>

        {/* Train/Test Percent (only for train) */}
        {formik.values.type === 'train' && (
          <Row>
            <Col>
              <Form.Group className='mb-3'>
                <FormLabel>Train Percent</FormLabel>
                <Form.Control
                  type='number'
                  min={0}
                  max={100}
                  name='train_percent'
                  value={formik.values.train_percent}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.train_percent && formik.errors.train_percent && <div className='text-danger'>{formik.errors.train_percent}</div>}
              </Form.Group>
            </Col>

            <Col>
              <Form.Group className='mb-3'>
                <FormLabel>Test Percent</FormLabel>
                <Form.Control
                  type='number'
                  min={0}
                  max={100}
                  name='test_percent'
                  value={formik.values.test_percent}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.test_percent && formik.errors.test_percent && <div className='text-danger'>{formik.errors.test_percent}</div>}
              </Form.Group>
            </Col>
          </Row>
        )}

        {formik.errors.form && <div className='text-danger mb-3'>{formik.errors.form}</div>}

        <div className='mt-2 d-flex align-items-center gap-2'>
          <button type='button' className='common-btn py-2 flex-grow-1' onClick={() => setStep((s) => s - 1)} disabled={isSubmitting}>
            Back
          </button>

          <button type='submit' className='common-btn py-2 flex-grow-1' disabled={isSubmitting}>
            {isSubmitting ? (
              <div className='d-flex align-items-center justify-content-center gap-2'>
                <Spinner animation='border' size='sm' />
                Submitting...
              </div>
            ) : (
              'Submit'
            )}
          </button>
        </div>
      </Form>

      <ResultsTable data={data} />
    </>
  )
}

export default TrainTestForm
